import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import BrandMark from '../components/BrandMark';
import { toast } from 'react-toastify';
import api from '../services/api';
import '../styles/auth.css';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validToken, setValidToken] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error('Token não encontrado');
      navigate('/login');
      return;
    }

    // Verificar se token é válido
    const verifyToken = async () => {
      try {
        const response = await api.get(`/auth/verify-reset-token/${token}`);
        setValidToken(response.data.valid);
        
        if (!response.data.valid) {
          toast.error('Link inválido ou expirado');
        }
      } catch (error) {
        console.error('Erro ao verificar token:', error);
        setValidToken(false);
        toast.error('Erro ao verificar token');
      }
    };

    verifyToken();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword: password
      });
      
      setSuccess(true);
      toast.success(response.data.message);
      
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao redefinir senha';
      toast.error(message);
      console.error('Erro ao redefinir senha:', error);
    } finally {
      setLoading(false);
    }
  };

  if (validToken === false) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-icon error">
              <AlertCircle size={32} />
            </div>
            <h1>Link Inválido</h1>
            <p>Este link de recuperação é inválido ou expirado</p>
          </div>

          <div className="error-message">
            <p>O link de recuperação de senha pode ter expirado ou já foi usado.</p>
            <p>Por favor, solicite um novo link de recuperação.</p>
          </div>

          <div className="auth-actions">
            <Link to="/forgot-password" className="btn btn-primary">
              Solicitar Novo Link
            </Link>
            <Link to="/login" className="btn btn-secondary">
              Voltar ao Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-icon success">
              <CheckCircle size={32} />
            </div>
            <h1>Senha Redefinida!</h1>
            <p>Sua senha foi alterada com sucesso</p>
          </div>

          <div className="success-message">
            <CheckCircle size={20} />
            <div>
              <p><strong>Senha redefinida com sucesso!</strong></p>
              <p>Sua senha foi alterada com segurança. Agora você pode fazer login com sua nova senha.</p>
            </div>
          </div>

          <div className="auth-actions">
            <Link to="/login" className="btn btn-primary">
              <Shield size={16} />
              Fazer Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (validToken === null) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <BrandMark tone="light" height={56} className="mx-auto mb-6 block" decorative />
            <h1>Verificando Link...</h1>
            <p>Aguarde enquanto verificamos a validade do seu link de recuperação</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <BrandMark tone="light" height={56} className="mx-auto mb-6 block" decorative />
          <h1>Definir Nova Senha</h1>
          <p>Digite uma senha segura para sua conta</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">Nova Senha</label>
            <div className="input-group">
              <Lock size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua nova senha"
                required
                disabled={loading}
                minLength={6}
              />
              <button
                type="button"
                className="input-icon-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Nova Senha</label>
            <div className="input-group">
              <Lock size={18} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme sua nova senha"
                required
                disabled={loading}
                minLength={6}
              />
              <button
                type="button"
                className="input-icon-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {password && confirmPassword && password !== confirmPassword && (
            <div className="form-error">
              As senhas não coincidem
            </div>
          )}

          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={loading || password !== confirmPassword}
          >
            {loading ? 'Redefinindo...' : 'Redefinir Senha'}
          </button>
        </form>

        <div className="auth-footer">
          <Link to="/login" className="auth-link">
            Voltar ao Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage; 